import { IProvider } from '@web3auth/modal';
import { Contract, TransactionReceipt, Web3, utils } from 'web3';
// import { type RegisteredSubscription } from 'web3/lib/commonjs/eth.exports';

import { ContractCallType, ContractSendType, SendContractType, SendErc721Type } from '@/types';
import Erc721Abi from './abi/Erc721Abi.json';
import Erc1155Abi from './abi/Erc1155Abi.json';
import { ERC721, MULTIPLIE_PERCENT, POLYGON_AMOY, POLYGON_MAIN } from '../common/name-space';
import networkList from '@/component/wallet/NetworkList';
import BigNumber from 'bignumber.js';
// import erc20abi from './erc20abi.json';

export default class EthereumRpc {
  private provider: IProvider;
  private web3: Web3;
  private contracts: Record<string, any>;
  private isInitialized: boolean = false;

  constructor(provider: IProvider) {
    this.provider = provider;
    this.web3 = new Web3(provider);
    this.contracts = {};
    // constructor에서는 비동기 초기화를 하지 않음
  }

  // 정적 팩토리 메서드 - 비동기 초기화를 위한 권장 방법
  static async create(provider: IProvider): Promise<EthereumRpc> {
    const instance = new EthereumRpc(provider);
    await instance.loadContract();
    return instance;
  }

  // 기존 constructor 호출을 위한 호환성 메서드
  static async initialize(provider: IProvider): Promise<EthereumRpc> {
    return this.create(provider);
  }

  async loadContract() {
    try {
      const chainId = process.env.NODE_ENV === 'development' ? POLYGON_AMOY : POLYGON_MAIN;
      const networks = await networkList(chainId);

      networks.forEach((network) => {
        const { symbol, token, abi } = network;

        if (abi && token) {
          const contract = new this.web3.eth.Contract(abi, token);
          this.contracts[symbol] = contract;
        } else {
          console.warn(`ABI or contract address not found for symbol: ${symbol}`);
        }
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('컨트랙트 로딩 중 오류 발생:', error);
      this.isInitialized = false;
    }
  }

  // 초기화 상태 확인
  isReady(): boolean {
    return this.isInitialized;
  }
  async contractStakingCall<T>(aPayload: ContractCallType): Promise<T> {
    const { method, symbol, data } = aPayload;
    const contract = this.contracts[symbol];
    const re: T = await contract.methods[method](...data).call();

    return re;
  }
  // 삭제예정
  async contractStakingSend<T>(aPayload: ContractCallType): Promise<T> {
    const { method, symbol, data } = aPayload;
    const contract = this.contracts[symbol];
    const re: T = await contract.methods[method](...data).send({});
    return re;
  }
  async contractCall<T>(aPayload: ContractSendType): Promise<any> {
    const { method, symbol, address } = aPayload;
    const re: T = await this.contracts[symbol].methods[method](address).call();
    return re;
  }
  async gasPrice() {
    const gasPrice = await this.web3.eth.getGasPrice();
    // console.log(`gasPrice: ${gasPrice}`);
    const increaseGasPrice = BigNumber(String(gasPrice)).multipliedBy(MULTIPLIE_PERCENT).toFixed(0);
    // console.log(`increaseGasPrice: ${increaseGasPrice}`);
    return increaseGasPrice;
  }

  async contractSend(aPayload: SendContractType): Promise<string> {
    const { method, symbol, from, data } = aPayload;
    const gasPrice = await this.gasPrice();
    const gasEstimate = await this.contracts[symbol].methods[method](...data).estimateGas({ from: from });
    const re: TransactionReceipt = await this.contracts[symbol].methods[method](...data).send({
      from: from,
      gas: gasEstimate.toString(),
      gasPrice,
    });
    return re.transactionHash.toString();
  }
  async sendNft(aPayload: SendErc721Type): Promise<string> {
    return aPayload.tokenType === ERC721 ? await this.send721(aPayload) : await this.send1155(aPayload);
  }
  private async send721(aPayload: SendErc721Type) {
    const { contractAddr, from, to, tokenId } = aPayload;
    const contract = new this.web3.eth.Contract(Erc721Abi, contractAddr);
    const gasPrice = await this.gasPrice();
    const gasEstimate = await contract.methods.safeTransferFrom(from, to, tokenId).estimateGas({ from: from });
    const re = await contract.methods
      .safeTransferFrom(from, to, tokenId)
      .send({ from: from, gas: gasEstimate.toString(), gasPrice });

    return re.transactionHash.toString();
  }
  private async send1155(aPayload: SendErc721Type) {
    const { contractAddr, from, to, tokenId } = aPayload;
    const contract = new this.web3.eth.Contract(Erc1155Abi, contractAddr);
    const amount = '1';
    const gasPrice = await this.gasPrice();
    const gasEstimate = await contract.methods
      .safeTransferFrom(from, to, tokenId, amount, '0x')
      .estimateGas({ from: from });

    const re = await contract.methods
      .safeTransferFrom(from, to, tokenId, amount, '0x')
      .send({ from: from, gas: gasEstimate.toString(), gasPrice });

    return re.transactionHash.toString();
  }
  async getAccounts(): Promise<string[]> {
    try {
      // provider 상태 확인
      if (!this.provider) {
        throw new Error('Provider not initialized');
      }

      // web3 인스턴스 상태 확인
      if (!this.web3 || !this.web3.eth) {
        throw new Error('Web3 not properly initialized');
      }

      const accounts = await this.web3.eth.getAccounts();

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      return accounts;
    } catch (error) {
      console.error('Error in getAccounts:', error);
      throw new Error(`Failed to get accounts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  async getAddress0(): Promise<string> {
    try {
      const accounts = await this.getAccounts();

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts available');
      }

      const address = accounts[0];

      if (!address) {
        throw new Error('First account address is undefined');
      }

      // 주소 형식 검증
      if (!this.web3.utils.isAddress(address)) {
        throw new Error('Invalid address format');
      }

      return address;
    } catch (error) {
      console.error('Error in getAddress0:', error);
      throw new Error(`Failed to get address: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  async getBalance(address: string): Promise<string> {
    try {
      const balance = await this.web3.eth.getBalance(address);
      if (BigNumber(String(balance)).isZero()) {
        return '0';
      }
      return this.web3.utils.fromWei(balance.toString(), 'ether') || '0';
    } catch (error) {
      console.log(error);
      return '0';
    }
  }
  async signAndSendTransaction(aToAddr: string, aAmount: string): Promise<string> {
    try {
      const web3 = new Web3(this.provider);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await this.gasPrice();

      const txRes = await web3.eth.sendTransaction({
        from: accounts[0],
        to: aToAddr,
        value: aAmount, // web3.utils.toWei('0.01', 'ether'),
        gasPrice: gasPrice,
      });
      return txRes.transactionHash.toString();
    } catch (error) {
      return error as string;
    }
  }
}
